import React, { useContext, useEffect, useMemo, useState } from 'react';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { EditorActions } from '@atlaskit/editor-core';
import { Inline } from '@atlaskit/primitives/compiled';
import { Show } from '@atlaskit/primitives/responsive';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { PopupPreview } from '@confluence/template-preview/entry-points/PopupPreview.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { isInEditorTemplatesPanelQueryParamValueOpenOrKeepOpen } from '@confluence/template-utils/entry-points/urlUtils.ts';
import { useShouldShowNewUserTemplates } from '@confluence/experiment-land-users-in-editor-with-revamped-bpqa/entry-points/useShouldShowNewUserTemplates.ts';
import { useEditorWidthObserver } from '@confluence/editor-features/entry-points/useEditorWidthObserver.ts';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';

import { useBlankPageQuickActionsContext } from './BlankPageQuickActionsContext.tsx';
import { QuickActionCard } from './QuickActionCard.tsx';
import { type PresetAPIType, QUICK_ACTIONS } from './bpqaConst.ts';
import { BlankPageQuickActionsCardContainer, BlankPageQuickActionsWrapper } from './bpqaStyled.tsx';
import type { QuickActionType } from './QuickActionCard.tsx';
import { QuickTemplateRow } from './QuickTemplateRow.tsx';
import { ShowMoreElementsLink } from './ShowMoreElementsLink.tsx';

export type BlankPageQuickActionsComponentProps = {
	editorActions: EditorActions;
	disabled: boolean;
	focusEditor: any;
	isTitleFocused: boolean | null;
	appearance: string | undefined;
	spaceKey: string;
	isUnpublishedDraft?: boolean;
	contentId: string;
	title: string | null;
	cancelDebouncedSaveDraft: () => void;
	showSpinner: () => void;
	hideSpinner: () => void;
	isAIEnabled: boolean;
	editorPresetAPI?: PresetAPIType;
	onTitleChange: (value: string) => void;
	/**
	 * When false, the template row (recommended templates + More templates button) is hidden.
	 * Defaults to true to preserve existing behaviour for all current callers.
	 */
	isTemplateBrowserEnabled?: boolean;
};

const BlankPageQuickActionsComponent = ({
	editorActions,
	disabled,
	isTitleFocused,
	focusEditor,
	appearance,
	spaceKey,
	isUnpublishedDraft,
	contentId,
	title,
	cancelDebouncedSaveDraft,
	showSpinner,
	hideSpinner,
	isAIEnabled,
	editorPresetAPI,
	onTitleChange,
	isTemplateBrowserEnabled = true,
}: BlankPageQuickActionsComponentProps) => {
	// New editor controls - rely on the typeAhead plugin state to deduce more elements link state instead
	const [isMoreElementsLinkActive, setIsMoreElementsLinkActive] = useState<boolean>(false);
	const isTypeAheadOpen = useSharedPluginStateSelector(editorPresetAPI, 'typeAhead.isOpen');
	const isAIModalOpen = useSharedPluginStateSelector(
		editorPresetAPI,
		'aiExperience.aiDecorationExperiencePluginState.isRunning',
	);
	const isShowingDiff = useSharedPluginStateSelector(
		editorPresetAPI,
		'trackChanges.isDisplayingChanges',
	);

	const isLivePage = useIsCurrentPageLive();
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const shouldTableBeFullWidth = appearance === 'full-width';
	const actions = QUICK_ACTIONS(shouldTableBeFullWidth);

	const { revampedBpqa } = useBlankPageQuickActionsContext();

	const { showNewUserTemplates } = useShouldShowNewUserTemplates();

	useEffect(() => {
		// we need to preload the PopupPreview
		// component or the rendering will flicker between
		// skeleton & loaded states
		void PopupPreview.preload();
	}, []);

	const handleMoreElementsClick = async () => {
		if (isTitleFocused) {
			await focusEditor();
		}

		// New editor controls - the type ahead is used to show all items
		if (editorExperiment('platform_editor_controls', 'variant1')) {
			if (isTypeAheadOpen) {
				editorPresetAPI?.typeAhead.actions.close({ insertCurrentQueryAsRawText: true });
			} else {
				editorPresetAPI?.quickInsert.actions.openTypeAhead(INPUT_METHOD.QUICK_INSERT);
			}
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'BlankPageQuickActions',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'blankPageQuickActionShowMoreLink',
					attributes: {
						version: '2',
					},
				},
			}).fire();
			return;
		}

		editorPresetAPI?.insertBlock?.actions?.toggleAdditionalMenu?.();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'BlankPageQuickActions',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'blankPageQuickActionShowMoreLink',
				attributes: {
					version: '2',
				},
			},
		}).fire();
		setIsMoreElementsLinkActive(true);
	};

	const observer = useMemo(
		() =>
			// eslint-disable-next-line no-restricted-syntax
			new IntersectionObserver((entries) => {
				setIsMoreElementsLinkActive(entries[0].isIntersecting);
			}),
		[],
	);

	useEffect(() => {
		// New editor controls - rely on the typeAhead plugin state to determine if menu is opened/closed
		if (editorExperiment('platform_editor_controls', 'variant1')) {
			return;
		}

		const observeElementBrowser = () => {
			const elementDrawer = document.querySelector('[data-testid="element-browser"]');

			if (elementDrawer) {
				observer.observe(elementDrawer);
			}
		};

		document.addEventListener('click', observeElementBrowser, false);
		return () => {
			document.removeEventListener('click', observeElementBrowser, false);
			observer.disconnect();
		};
	}, [observer]);

	const { width } = useEditorWidthObserver({});
	const shouldAdjustForContextPanel = width > 1175;
	const { getQueryParams } = useContext(RoutesContext);
	const isTemplatesPanelOpen =
		isInEditorTemplatesPanelQueryParamValueOpenOrKeepOpen(getQueryParams);

	// Hide the revamped BPQA when the templates panel is open
	if (revampedBpqa && isTemplatesPanelOpen) {
		return null;
	}

	// When the new AIFC AI modal is opened or when showing diff, we want to hide the BPQA
	if ((isAIEnabled && isAIModalOpen && fg('aifc_create_enabled')) || isShowingDiff) {
		return null;
	}

	return (
		<BlankPageQuickActionsWrapper
			data-testid="blank-page-quick-actions-container"
			data-vc="blank-page-quick-actions-container"
			{...ssrPlaceholderIdProp}
		>
			<BlankPageQuickActionsCardContainer
				isTemplatesPanelOpen={isTemplatesPanelOpen}
				shouldAdjustForContextPanel={shouldAdjustForContextPanel}
			>
				{/*Show Templates & Import for drafts/live pages by default, and also for published pages when
			 `cc_snippets` is enabled.
			 On embedded pages, the template row is hidden when isTemplateBrowserEnabled is false.
			*/}
				{(isUnpublishedDraft || isLivePage || expVal('cc_snippets', 'isEnabled', false)) &&
					(!fg('confluence_embedded_decouple_template_browser_bpqa') ||
						isTemplateBrowserEnabled) && (
						<QuickTemplateRow
							cancelDebouncedSaveDraft={cancelDebouncedSaveDraft}
							contentId={contentId}
							disabled={disabled}
							showSpinner={showSpinner}
							hideSpinner={hideSpinner}
							spaceKey={spaceKey}
							title={title}
							editorActions={editorActions}
							onTitleChange={onTitleChange}
						/>
					)}

				{revampedBpqa || showNewUserTemplates ? null : (
					<Inline space="space.200" alignInline="start" alignBlock="center" shouldWrap>
						{actions?.map((quickAction: QuickActionType, idx) => {
							// hide third card below 768px and hide all cards below 480px
							const displayThreshold = idx === actions.length - 1 ? 'sm' : 'xs';
							return (
								<Show above={displayThreshold} key={quickAction.id}>
									<QuickActionCard
										quickAction={quickAction}
										editorActions={editorActions}
										createAnalyticsEvent={createAnalyticsEvent}
										disabled={disabled}
										focusEditor={focusEditor}
										editorPresetAPI={editorPresetAPI}
									/>
								</Show>
							);
						})}
						<ShowMoreElementsLink
							disabled={disabled}
							handleMoreElementsClick={handleMoreElementsClick}
							isActive={
								editorExperiment('platform_editor_controls', 'variant1')
									? !!isTypeAheadOpen
									: isMoreElementsLinkActive
							}
						/>
					</Inline>
				)}
			</BlankPageQuickActionsCardContainer>
		</BlankPageQuickActionsWrapper>
	);
};

export const BlankPageQuickActions = withErrorBoundary({
	attribution: Attribution.PAGE_EXTENSIONS,
})(BlankPageQuickActionsComponent);
